import type { ReactNode } from 'react';

import type { PermitAction } from 'hooks/usePermissions';
import { usePermissions } from 'hooks/usePermissions';

type Props = {
  children: ReactNode;
  requiredPermission?: PermitAction;
};

/**
 * Renders `children` only when the current user's role satisfies
 * `requiredPermission`; otherwise renders nothing.
 *
 * Wrap any UI that triggers a mutation or exposes a role-gated affordance:
 * create/upload/edit/delete buttons, export actions, sidebar entries for
 * restricted areas, and detail-page action menus. For route-level gating
 * use `RequirePermission` instead.
 *
 * Note: omitting `requiredPermission` renders children unconditionally.
 *
 * @example
 *   <Restricted requiredPermission="payouts:manage">
 *     <PrimaryButton onClick={...}>Create payout</PrimaryButton>
 *   </Restricted>
 */
export const Restricted = ({ children, requiredPermission }: Props) => {
  const { isPermitted } = usePermissions();

  if (!requiredPermission || isPermitted(requiredPermission)) {
    return <>{children}</>;
  }

  return null;
};
